import {
  LegendItemModel,
  ViewLegendSection,
  formattingSections,
  componentsLegendRow,
  referencesLegendRow,
  formattingLegendRow,
  ActiveConditionalFormattingForLegend,
} from '@ardoq/view-legend';
import { isTruthy } from '@ardoq/common-helpers';
import { LegendComponentType, LegendReferenceType } from '@ardoq/graph';
import { dispatchAction } from '@ardoq/rxbeach';
import { type I18nLocale } from '@ardoq/i18n';
import {
  graphViewLegendHighlightComponentType,
  graphViewLegendHighlightConditionalFormatting,
  graphViewLegendHighlightReferenceType,
  graphViewLegendSetSelectedComponentTypes,
  graphViewLegendSetSelectedConditionalFormatting,
  graphViewLegendSetSelectedReferenceTypes,
} from './actions';

export type GetAdditionalRowsForGraphViewLegendArgs = {
  referenceTypes: LegendReferenceType[];
  componentTypes: LegendComponentType[];
  hasCollapsedNodes: boolean;
  hasReferenceParents: boolean;
  hasNonComponentNodes: boolean;
  isUserDefinedGrouping: boolean;
  showComponentSwatches: boolean;
  activeConditionalFormattingForLegend: ActiveConditionalFormattingForLegend;
  showComponentShapes: boolean;
  showReferencesAsLines: boolean;
  selectableItems: boolean;
  selectedConditionalFormattingIds?: string[];
  isNewConditionalFormatting: boolean;
  i18nLocale: I18nLocale;
};

export const getAdditionalRowsForGraphViewLegend = ({
  componentTypes,
  referenceTypes,
  hasCollapsedNodes,
  hasReferenceParents,
  hasNonComponentNodes,
  isUserDefinedGrouping,
  showComponentSwatches,
  activeConditionalFormattingForLegend,
  showComponentShapes,
  showReferencesAsLines,
  selectableItems,
  selectedConditionalFormattingIds,
  isNewConditionalFormatting,
  i18nLocale,
}: GetAdditionalRowsForGraphViewLegendArgs) =>
  [
    componentsLegendRow({
      componentTypes,
      hasCollapsedNodes,
      hasReferenceParents,
      hasNonComponentNodes,
      isUserDefinedGrouping,
      showComponentSwatches,
      showComponentShapes,
      selectableItems,
      onComponentMouseOver,
      setSelectedComponentItems,
      isComponentTypesHeadingVisible: Boolean(
        hasCollapsedNodes || hasReferenceParents || showComponentShapes
      ),
      i18nLocale,
    }),
    referencesLegendRow({
      referenceTypes,
      showReferencesAsLines,
      selectableItems,
      onReferenceMouseOver,
      setSelectedReferenceItems,
      i18nLocale,
    }),
    formattingLegendRow({
      selectableItems,
      getSelectedItemsSetter,
      selectableFormattingSection,
      i18nLocale,
      formatting: formattingSections(
        activeConditionalFormattingForLegend,
        !isNewConditionalFormatting
      ),
      selectedIds: selectedConditionalFormattingIds
        ? new Set(selectedConditionalFormattingIds)
        : undefined,
    }),
  ].filter(isTruthy);

const selectableFormattingSection = (
  section: ViewLegendSection,
  selectedIds: Set<string> | null,
  setSelectedItems: (items: LegendItemModel[]) => void
): ViewLegendSection => ({
  ...section,
  selectableItems: true,
  setSelectedItems,
  items: section.items.map(item => ({
    ...item,
    isSelected: item.id ? (selectedIds?.has(item.id) ?? false) : false,
    onMouseOver: () =>
      dispatchAction(
        graphViewLegendHighlightConditionalFormatting({ id: item.id || '' })
      ),
    onMouseOut: () =>
      dispatchAction(
        graphViewLegendHighlightConditionalFormatting({ id: null })
      ),
  })),
});

const getSelectedItemsSetter = (
  formatting: ViewLegendSection[],
  selectedIds?: Set<string>
) => {
  const selectedIdsBySection = new Map(
    formatting.map(section => [
      section,
      section.items
        .filter(({ id }) => id && selectedIds?.has(id))
        .map(({ id }) => id),
    ])
  );
  /** legend sections were sort of designed to each have their own independent selection. but in the formatting legend, we can have a selection spanning multiple sections. this function serves to manage the consolidated selection of all sections under the formatting legend row. */
  return (section: ViewLegendSection) => (items: LegendItemModel[]) => {
    const selectedIdsFromThisSection = new Set(items.map(({ id }) => id));
    const selectionFromThisSection = section.items
      .filter(({ id }) => id && selectedIdsFromThisSection.has(id))
      .map(({ id }) => id)
      .filter(isTruthy);
    selectedIdsBySection.set(section, Array.from(selectedIdsFromThisSection));
    const selectionFromOtherSections = [...selectedIdsBySection.entries()]
      .filter(([currentSection]) => currentSection !== section)
      .flatMap(([, filteredItems]) => filteredItems.filter(isTruthy));
    dispatchAction(
      graphViewLegendSetSelectedConditionalFormatting({
        ids: [...selectionFromThisSection, ...selectionFromOtherSections],
      })
    );
  };
};

const setSelectedComponentItems = (
  items: LegendItemModel[],
  componentTypes: LegendComponentType[]
) => {
  const selectedIds = new Set(items.map(({ id }) => id));
  dispatchAction(
    graphViewLegendSetSelectedComponentTypes({
      componentTypes: componentTypes.filter(({ ids }) =>
        selectedIds.has(ids.join(','))
      ),
    })
  );
};
const onComponentMouseOver = (componentType: LegendComponentType | null) =>
  dispatchAction(
    graphViewLegendHighlightComponentType({
      componentType,
    })
  );

const setSelectedReferenceItems = (
  items: LegendItemModel[],
  referenceTypes: LegendReferenceType[]
) => {
  const selectedIds = new Set(items.map(({ id }) => id));
  dispatchAction(
    graphViewLegendSetSelectedReferenceTypes({
      referenceTypes: referenceTypes.filter(({ globalTypeIds }) =>
        selectedIds.has(globalTypeIds.join(','))
      ),
    })
  );
};
const onReferenceMouseOver = (referenceType: LegendReferenceType | null) =>
  dispatchAction(
    graphViewLegendHighlightReferenceType({
      referenceType,
    })
  );
